/**
 * Map-click lesson assessment — pure logic for the `map_click` question type.
 *
 * Roadmap H1.1: students answer "Click on <country/continent>" questions by
 * clicking the globe. This module owns the framework-free pieces so they can
 * be unit-tested without a map:
 *
 *   - resolveClickedCountry(lng, lat, countriesGeojson)
 *       point-in-polygon lookup against the loaded Natural Earth countries —
 *       works no matter which layers are currently rendered on the globe
 *   - evaluateMapClick(question, clicked)
 *       matches the clicked feature against the question's target
 *       (case- and diacritic-insensitive, name or ISO3, continent aware)
 *   - getMapClickAttempts(question)
 *       attempts budget for a question (default 3)
 *
 * Question schema (see lesson-schema.js):
 *   {
 *     "type": "map_click",
 *     "prompt": "Click on Africa!",
 *     "target": { "kind": "country" | "continent", "name": "France", "iso": "FRA" },
 *     "attempts": 3
 *   }
 */
import { pointInPolygon } from '../utils/geo-utils.js'

export const DEFAULT_MAP_CLICK_ATTEMPTS = 3

// Natural Earth labels the Australian landmass's continent 'Oceania', but
// K-2 curricula teach it as the continent 'Australia'. Accept either name.
const CONTINENT_ALIASES = {
  australia: 'oceania',
  oceania: 'australia',
}

/**
 * Normalize a string for fuzzy matching: strip diacritics, lowercase, trim.
 * Same normalization contract as admin-boundaries.js#normalizeForMatch.
 */
export function normalizeForMatch(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

// Natural Earth encodes some sovereign states (France, Norway, Kosovo, ...)
// with ISO_A3 === '-99'; the real code lives in ADM0_A3 (same fallback as
// natural-earth.js#_getIso3).
function getIso3(props) {
  const primary = props.iso3 || props.ISO_A3 || props.iso_a3 || ''
  if (primary && primary !== '-99') return primary
  return props.ADM0_A3 || props.adm0_a3 || ''
}

function getName(props) {
  return props.name_en || props.NAME || props.name || props.admin || ''
}

function getContinent(props) {
  return props.continent || props.CONTINENT || ''
}

/**
 * Resolve a globe click to a country via point-in-polygon against the
 * Natural Earth countries FeatureCollection loaded at startup.
 *
 * @param {number} lng - Clicked longitude
 * @param {number} lat - Clicked latitude
 * @param {Object} countriesGeojson - Natural Earth countries FeatureCollection
 * @returns {{ name: string, iso: string, continent: string }|null} null = ocean / no match
 */
export function resolveClickedCountry(lng, lat, countriesGeojson) {
  if (!countriesGeojson?.features || !Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null
  }

  for (const feature of countriesGeojson.features) {
    const geom = feature?.geometry
    if (!geom) continue

    let hit = false
    if (geom.type === 'Polygon') {
      hit = pointInPolygon(lng, lat, geom.coordinates)
    } else if (geom.type === 'MultiPolygon') {
      hit = geom.coordinates.some((polygon) => pointInPolygon(lng, lat, polygon))
    }

    if (hit) {
      const props = feature.properties || {}
      return {
        name: getName(props),
        iso: String(getIso3(props)).toUpperCase(),
        continent: getContinent(props),
      }
    }
  }

  return null
}

function continentMatches(clickedContinent, targetName) {
  const clicked = normalizeForMatch(clickedContinent)
  const target = normalizeForMatch(targetName)
  if (!clicked || !target) return false
  if (clicked === target) return true
  return CONTINENT_ALIASES[target] === clicked
}

/**
 * Evaluate a clicked feature against a map_click question's target.
 * Pure function — no globe or engine state.
 *
 * @param {Object} question - The map_click question ({ target: { kind, name, iso? } })
 * @param {{ name?: string, iso?: string, continent?: string }|null} clicked - Resolved click (null = ocean)
 * @returns {{ correct: boolean, clickedName: string }}
 */
export function evaluateMapClick(question, clicked) {
  const target = question?.target || {}
  const clickedName = clicked?.name || ''

  if (!clicked) {
    return { correct: false, clickedName }
  }

  if (target.kind === 'continent') {
    return { correct: continentMatches(clicked.continent, target.name), clickedName }
  }

  // Default: country target — accept a name match or an ISO3 match
  const nameMatch = Boolean(
    target.name && normalizeForMatch(clicked.name) === normalizeForMatch(target.name)
  )
  const isoMatch = Boolean(
    target.iso && String(clicked.iso || '').toUpperCase() === String(target.iso).toUpperCase()
  )

  return { correct: nameMatch || isoMatch, clickedName }
}

/**
 * Attempts budget for a map_click question (default 3).
 * @param {Object} question
 * @returns {number}
 */
export function getMapClickAttempts(question) {
  const n = Number(question?.attempts)
  return Number.isInteger(n) && n > 0 ? n : DEFAULT_MAP_CLICK_ATTEMPTS
}
